import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { LOCATIONS, LOCATION_IMAGES } from "@/data/locations";
import { BUSINESS } from "@/data/businessInfo";
import { SEO } from "@/components/SEO";
import { ServiceAreaMap } from "@/components/GoogleMap";
import { ServiceCardsSection } from "@/components/SharedSections";
import { motion } from "framer-motion";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function ServiceAreasPage() {
  return (
    <div>
      <SEO title="Electrician Service Areas - Treasure Coast & Palm Beach County FL | Southern Light Solutions" description="Southern Light Solutions serves Port St. Lucie, Fort Pierce, Stuart, Vero Beach, Sebastian, West Palm Beach, Jupiter, Palm Beach Gardens, and all communities across St. Lucie, Martin, Indian River, and Palm Beach counties." path="/service-areas" />
      <section className="bg-[#FAFAFA] py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#317E8C] mb-3 block" style={{ fontFamily: 'Outfit, sans-serif' }}>Service Areas</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#401303] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Serving the Treasure Coast & Beyond</h1>
            <p className="mt-4 text-base lg:text-lg text-[#6B3A1F] max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>Southern Light Solutions proudly provides residential and commercial electrical services across St. Lucie, Martin, Indian River, and Palm Beach counties.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {LOCATIONS.map((loc, i) => (
              <motion.div key={loc.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.3, delay: i * 0.04 }}>
                <Link to={`/service-areas/${loc.id}`} className="block bg-white rounded-2xl border border-[#6B3A1F]/10 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group" data-testid={`area-card-${loc.id}`}>
                  <div className="h-40 overflow-hidden relative">
                    <img src={LOCATION_IMAGES[loc.id] || LOCATION_IMAGES["port-st-lucie"]} alt={`Electrical services in ${loc.name}, Florida`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#401303]/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#BF8D30] flex-shrink-0" />
                        <h2 className="text-white text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{loc.name}</h2>
                      </div>
                      <p className="text-white/70 text-xs mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>{loc.county}</p>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-[#6B3A1F] leading-relaxed line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{loc.content.substring(0, 150)}...</p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-[#317E8C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      View Details <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Map */}
          <div className="mt-10">
            <ServiceAreaMap className="h-[400px]" />
          </div>
        </div>
      </section>

      <ServiceCardsSection title="Electrical Services Available" />

      <section className="bg-warm-gradient py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#401303] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Don't See Your Area?</h2>
          <p className="text-white/80 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>We may still be able to help. Call us and we'll let you know if we serve your location.</p>
          <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-[#401303] font-bold rounded-full text-sm" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="areas-call-btn">
            <Phone className="w-4 h-4" /> {BUSINESS.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
